import { faLongArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';

import Form, { SubmitType } from '../../../components/form';
import Button from '../../../components/form/elements/button';
import Group from '../../../components/form/elements/group';
import Link from '../../../components/form/elements/link';
import {
  LIVESTOCK_FODDER_NEED_EDIT,
  LIVESTOCK_FODDER_NEED_INDEX,
} from '../../../router/routes';
import { can, route } from '../../../services/utils';
import { FormData } from './create';
import Summary from './summary';
import FodderNeedPeriodType from '../../../enums/fodder-need-period-type';
import FodderNeed from './../../../interfaces/fodder-need';
import { Option } from '../../../components/form/elements/select';
import OptionModal from '../../../components/modals/option-modal';
import { useService } from '../../../services/services';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';

export interface SummaryFeedingstuffRow {
  feedingstuff_id: string;
  feedingstuff_eat_losses: number;
  feedingstuff_storage_losses: number;
}

export interface SummarySubmitData {
  summary: { [key: string]: { [key: string]: SummaryFeedingstuffRow } };
}

export default function StepSummary({
  formData,
  onReturn,
  onSubmit,
  periods,
  saveFormData,
  classifiers = null,
  onlyTotal = false,
}: {
  formData: FormData;
  onReturn?: () => void;
  onSubmit?: SubmitType<null, FodderNeed>;
  onEdit?: () => void;
  onDelete?: () => void;
  periods: string[];
  saveFormData?: Function;
  classifiers?: Option[];
  onlyTotal?: boolean;
}) {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.livestock.fodder_need.'.concat(key)),
    [t]
  );
  const [modal, setModal] = useState(null);
  const { fodderNeedService } = useService();
  const history = useHistory();
  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const deleteFodderNeedModal = useCallback(() => {
    return (
      <OptionModal
        title={tp('delete')}
        content={tp('confirm_delete')}
        buttons={[
          { title: t('yes'), name: 'yes', value: true },
          { title: t('no'), name: 'no', value: false },
        ]}
        onChoice={(choice) => {
          if (choice) {
            fodderNeedService
              .destroy(farmUser.farm_id, formData.id)
              .subscribe(() => {
                history.push(
                  route(LIVESTOCK_FODDER_NEED_INDEX, {
                    farmId: farmUser.farm_id,
                  })
                );
              });
          }

          setModal(null);
        }}
        onClose={() => setModal(null)}
      />
    );
  }, [t, tp, fodderNeedService, farmUser.farm_id, history, formData.id]);

  const onDelete = useCallback(() => {
    setModal(deleteFodderNeedModal);
  }, [deleteFodderNeedModal]);

  const usedPeriods = useMemo(
    () =>
      Array.from(
        new Set(
          Object.values(formData.periods)
            .filter((value: any) => {
              return value.from && value.until;
            })
            .map((value: any) =>
              !value.isSummerPeriod
                ? FodderNeedPeriodType.WINTER
                : FodderNeedPeriodType.SUMMER
            )
            .sort((a, b) => (a > b ? -1 : 1))
        )
      ),
    [formData.periods]
  );

  const [body] = useState(() => [
    Group(
      [
        Summary(
          'summary',
          periods,
          usedPeriods,
          formData,
          saveFormData,
          classifiers,
          onlyTotal
        ),
      ],
      tp('total_need')
    ),
  ]);

  const [footer] = useState(() => {
    if (onlyTotal) {
      const btns = [];

      if (can(farmUser, { title: 'update fodder_need' }))
        btns.push(
          Button('edit', () =>
            history.push(
              route(LIVESTOCK_FODDER_NEED_EDIT, {
                farmId: farmUser.farm_id,
                id: formData.id,
              })
            )
          )
        );

      if (can(farmUser, { title: 'delete fodder_need' }))
        btns.push(Button('delete', onDelete));

      return btns;
    }
    return [
      Button('return', onReturn, tp('return'), faLongArrowLeft, null, true),
      Link(
        route(LIVESTOCK_FODDER_NEED_INDEX, { farmId: farmUser.farm_id }),
        'cancel'
      ),
      Button(formData.id ? 'save' : 'create'),
    ];
  });

  const schema = (schema: any) => {
    let obj = {};
    usedPeriods.forEach((element) => {
      Object.assign(obj, {
        [element]: schema.lazy((value: any) => {
          let summarySchema = {};
          if (value !== undefined) {
            Object.keys(value).forEach((summaryFeedingstuffKey: string) => {
              //animal group validation
              Object.assign(summarySchema, {
                [summaryFeedingstuffKey]: schema.object().shape({
                  feedingstuff_eat_losses: schema
                    .number()
                    .required()
                    .integer()
                    .min(0),
                  feedingstuff_storage_losses: schema
                    .number()
                    .required()
                    .integer()
                    .min(0),
                }),
              });
            });
            return schema.object().shape(summarySchema);
          } else {
            return schema.object().strip();
          }
        }),
      });
    });

    return schema.object().shape({ summary: yup.object().shape(obj) });
  };

  return (
    <div>
      <Form
        title={tp('fodder_need_calculation')}
        mode={'create'}
        backTo={route(LIVESTOCK_FODDER_NEED_INDEX, {
          farmId: farmUser.farm_id,
        })}
        body={body}
        footer={footer}
        values={formData}
        submit={onSubmit}
        schema={schema(yup)}
      />
      {modal}
    </div>
  );
}
